import { useEffect } from 'react';

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  image,
  type = 'website',
  robots = 'index, follow',
  author,
  article,
  structuredData,
  structuredDataId = 'seo-structured-data'
}) => {
  useEffect(() => {
    const baseTitle = 'Boxd Logistics';
    const siteUrl = 'https://boxdlogistics.in';
    const resolvedCanonical = canonical || `${siteUrl}${window.location.pathname}`;
    const resolvedImage = image || `${siteUrl}/logo.jpg`;
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;

    const setMeta = (selector, attr, key, value) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    const setLink = (selector, rel, href) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    if (description) setMeta('meta[name="description"]', 'name', 'description', description);
    if (keywords) setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    if (robots) setMeta('meta[name="robots"]', 'name', 'robots', robots);
    if (author) setMeta('meta[name="author"]', 'name', 'author', author);

    setLink('link[rel="canonical"]', 'canonical', resolvedCanonical);

    setMeta('meta[property="og:title"]', 'property', 'og:title', title ? `${title} | ${baseTitle}` : baseTitle);
    if (description) setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', type);
    setMeta('meta[property="og:url"]', 'property', 'og:url', resolvedCanonical);
    setMeta('meta[property="og:image"]', 'property', 'og:image', resolvedImage);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', baseTitle);
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', 'en_IN');

    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title ? `${title} | ${baseTitle}` : baseTitle);
    if (description) setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', resolvedImage);

    if (article?.publishedTime) {
      setMeta(
        'meta[property="article:published_time"]',
        'property',
        'article:published_time',
        article.publishedTime
      );
    }
    if (article?.modifiedTime) {
      setMeta(
        'meta[property="article:modified_time"]',
        'property',
        'article:modified_time',
        article.modifiedTime
      );
    }
    if (article?.section) {
      setMeta('meta[property="article:section"]', 'property', 'article:section', article.section);
    }
    if (article?.tags?.length) {
      setMeta('meta[property="article:tag"]', 'property', 'article:tag', article.tags.join(', '));
    }

    const jsonLdId = structuredDataId;
    const existingScript = document.getElementById(jsonLdId);
    if (structuredData) {
      const script = existingScript || document.createElement('script');
      script.type = 'application/ld+json';
      script.id = jsonLdId;
      script.text = JSON.stringify(structuredData);
      if (!existingScript) document.head.appendChild(script);
    } else if (existingScript) {
      existingScript.remove();
    }
  }, [title, description, keywords, canonical, image, type, robots, author, article, structuredData, structuredDataId]);

  return null;
};

export default SEO;
